import { useState } from 'react';
import { Button } from 'src/components/atoms/Button';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { ConfirmDialog } from 'src/components/molecules/ConfirmDialog';
import { useMODataContext } from 'src/features/MO/contexts/MODataContext';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useFontErrorColor } from 'src/hooks/colors/useErrorColor';

export type DeleteOptimizationButtonProps = {
  optimizationId: string;
};

export const DeleteOptimizationButton: React.FC<
  DeleteOptimizationButtonProps
> = ({ optimizationId }) => {
  const { deleteOptimization } = useMODataContext();
  const { toastState, showError, hideToast } = useCustomToast();
  const color = useFontErrorColor();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleting) {
      return;
    }

    setDeleting(true);

    try {
      await deleteOptimization(optimizationId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Deletion failed', e);
      showError(
        'Delete failed',
        'Failed to delete optimization. Please try again.',
      );
      // On success this button gets unmounted
      setDeleting(false);
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    handleDelete();
  };

  return (
    <>
      <Button
        buttonName="delete-optimization"
        isLoading={deleting}
        onClick={() => setConfirmOpen(true)}
        color={color}
        variant={'outline'}>
        Delete
      </Button>
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        headerText="Delete Optimization"
        bodyText="Are you sure? You can't undo this action afterwards."
        confirmText="Delete"
      />
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
    </>
  );
};
